/**
 * uni-push 通用插件 —— 通知提醒（声音/震动）
 *
 * 职责：收到在线消息（前台/锁屏/后台进程存活）时，按消息字段播放提示音、触发震动。
 * - 默认提示音 = 手机系统消息提示音（原生 API 播放，无需内置音频文件）
 * - 自定义声音 = InnerAudioContext 播放宿主项目自带的音频文件
 * - App 内设置开关（声音/震动）由宿主项目通过 PushAlertSettings 注入，作为全局闸门
 *
 * 本文件不依赖任何项目代码，可随插件目录整体复制到其他项目。
 */
import type { PushAlertOptions, PushAlertSettings } from './types';

/** 默认提醒设置（宿主项目未注入设置时视为全部开启） */
export const DEFAULT_ALERT_SETTINGS: PushAlertSettings = {
  soundEnabled: true,
  vibrateEnabled: true,
};

/** 自定义声音播放器单例（新消息到达打断上一条重播，防叠播） */
let customAudio: ReturnType<typeof uni.createInnerAudioContext> | null = null;

function getAudioContext() {
  if (!customAudio) {
    customAudio = uni.createInnerAudioContext();
    customAudio.onError(err => console.warn('[UniPush] 自定义声音播放失败', err));
  }
  return customAudio;
}

/** 上一条系统铃声实例句柄（新消息到达先停掉，防叠播） */
let lastRingtone: any = null;

/**
 * 播放系统默认消息提示音（无需内置音频文件）
 * - Android：RingtoneManager.TYPE_NOTIFICATION，跟随用户系统设置的默认通知铃声
 * - iOS：AudioToolbox 系统声音（1003 = 短信收到音），遵循静音拨片
 * 仅 App 端可用；任何异常静默降级（只打日志，不影响业务）
 */
export function playSystemNotificationSound() {
  // #ifdef APP-PLUS
  try {
    if (plus.os.name === 'Android') {
      const RingtoneManager = plus.android.importClass('android.media.RingtoneManager') as any;
      // TYPE_NOTIFICATION = 2
      const uri = RingtoneManager.getDefaultUri(2);
      const ringtone = RingtoneManager.getRingtone(plus.android.runtimeMainActivity(), uri);
      if (!ringtone) return;
      // importClass 静态方法返回的是原生实例，不能用 JS 直接调方法，需 plus.android.invoke
      try {
        if (lastRingtone) plus.android.invoke(lastRingtone, 'stop');
      }
      catch { /* 上一条已停或句柄失效，忽略 */ }
      lastRingtone = ringtone;
      plus.android.invoke(ringtone, 'play');
      return;
    }
    // iOS：导入 AudioToolbox 框架并调用 C 函数 AudioServicesPlaySystemSound
    const AudioToolbox = plus.ios.import('AudioToolbox') as any;
    AudioToolbox?.AudioServicesPlaySystemSound?.(1003);
  }
  catch (e) {
    console.warn('[UniPush] 系统提示音播放失败', e);
  }
  // #endif
}

/**
 * 播放自定义声音文件（宿主项目 static 资源路径，如 '/static/audio/new-order.mp3'）
 */
export function playCustomSound(src: string) {
  if (!src) return;
  try {
    const audio = getAudioContext();
    audio.stop(); // 防叠播：先停止上一条
    audio.src = src;
    audio.play();
  }
  catch (e) {
    console.warn('[UniPush] 自定义声音播放失败', src, e);
  }
}

/**
 * 通知提醒统一入口（声音 + 震动）
 *
 * 判定规则：
 * 1. customSound 非空：forceSound=true 直接播放（绕过设置开关）；否则需 settings.soundEnabled 开启
 * 2. 否则 sound=true 且 settings.soundEnabled 开启：播放系统默认提示音
 * 3. vibrate=true 且 settings.vibrateEnabled 开启：长震动
 *
 * @param options 单条消息的提醒配置（通常来自后端 payload 的 sound/customSound/forceSound/vibrate 字段）
 * @param settings App 内设置开关（宿主项目注入，不传视为全部开启）
 */
export function playPushAlert(options: PushAlertOptions = {}, settings?: Partial<PushAlertSettings>) {
  const s = { ...DEFAULT_ALERT_SETTINGS, ...settings };

  // 声音：自定义声音优先于默认提示音
  if (options.customSound) {
    if (options.forceSound || s.soundEnabled) {
      playCustomSound(options.customSound);
    }
  }
  else if (options.sound && s.soundEnabled) {
    playSystemNotificationSound();
  }

  // 震动
  if (options.vibrate && s.vibrateEnabled) {
    uni.vibrateLong({ fail: err => console.warn('[UniPush] 震动触发失败', err) });
  }
}
